/** @jsx jsx */
import { css, jsx } from '@emotion/core';
import { Fragment, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { Modal, Button, Select, InputNumber, Form } from 'antd';

import { setLuoxData, setLuoxUnits } from 'redux/actions';

import { convertLuoxData } from 'utils/getLuoxData';

const { Option } = Select;

const EditAlphaOpicData = ({ measurementName }) => {
  const dispatch = useDispatch();
  const location = useLocation();

  const luoxData = useSelector((state) => state.dataReducers.luoxData);
  const { luoxInput, luoxOutput } = luoxData;
  const luoxUnits = useSelector((state) => state.dataReducers.luoxUnits);
  const {
    absoluteOrRelative,
    radianceOrIrradiance,
    powerScale,
    areaScale,
    relativePower,
  } = luoxUnits;

  useEffect(() => {
    if (luoxInput && luoxUnits) {
      const luoxData = convertLuoxData(luoxInput, luoxUnits);
      dispatch(setLuoxData(luoxData));
    }
  }, [absoluteOrRelative, powerScale, areaScale, relativePower]);

  const handleRelativeOrAbsolute = (value) => {
    const newLuoxUnits = Object.assign({}, luoxUnits, {
      absoluteOrRelative: value,
    });
    dispatch(setLuoxUnits(newLuoxUnits));
  };

  const handleRadianceOrIrradiance = (value) => {
    const newLuoxUnits = Object.assign({}, luoxUnits, {
      radianceOrIrradiance: value,
    });
    dispatch(setLuoxUnits(newLuoxUnits));
  };

  const handlePowerScale = (value) => {
    const newLuoxUnits = Object.assign({}, luoxUnits, {
      powerScale: value,
    });
    dispatch(setLuoxUnits(newLuoxUnits));
  };

  const handleAreaScale = (value) => {
    const newLuoxUnits = Object.assign({}, luoxUnits, {
      areaScale: value,
    });
    dispatch(setLuoxUnits(newLuoxUnits));
  };

  const handleLuminanceOrIlluminance = (value) => {
    const newLuoxUnits = Object.assign({}, luoxUnits, {
      radianceOrIrradiance: value === 'luminance' ? 'radiance' : 'irradiance',
    });
    dispatch(setLuoxUnits(newLuoxUnits));
  };

  const handleRelativePower = (value) => {
    const newLuoxUnits = Object.assign({}, luoxUnits, {
      relativePower: value,
    });
    dispatch(setLuoxUnits(newLuoxUnits));
  };

  return (
    <Form
      css={alphaOpicDataForm(location.pathname)}
      initialValues={{ remember: true }}
    >
      {location.pathname !== '/results' && <h4>Alpha-Opic Data</h4>}
      <Form.Item label='SPD Type' name='spdType'>
        <Select
          defaultValue={absoluteOrRelative}
          onChange={handleRelativeOrAbsolute}
        >
          <Option value={'absolute'}>Absolute</Option>
          <Option value={'relative'}>Relative</Option>
        </Select>
      </Form.Item>
      {absoluteOrRelative === 'absolute' && (
        <Fragment>
          <Form.Item label='Measurement Type' name='measurementType'>
            <Select
              defaultValue={radianceOrIrradiance}
              onChange={handleRadianceOrIrradiance}
            >
              <Option value='irradiance'>irradiance (lx)</Option>
              <Option value='radiance'>radiances (cd/m²)</Option>
            </Select>
          </Form.Item>
          <Form.Item label='Measurement Units' name='measurementUnits'>
            <Select defaultValue={powerScale} onChange={handlePowerScale}>
              <Option value='µW'>µW</Option>
              <Option value='mW'>mW</Option>
              <Option value='W'>W</Option>
            </Select>
          </Form.Item>
          <Form.Item label='Per' name='per'>
            <Select defaultValue={areaScale} onChange={handleAreaScale}>
              <Option value='mm²'>mm²</Option>
              <Option value='cm²'>cm²</Option>
              <Option value='m²'>m²</Option>
            </Select>
          </Form.Item>
          {/* {radianceOrIrradiance === 'radiance' && 'per sr.'} */}
        </Fragment>
      )}
      {absoluteOrRelative === 'relative' && (
        <Fragment>
          <Form.Item
            label='Measurement Type'
            name='measurementType'
            tooltip='With a relative SPD, an additional measurement is needed in order to calculate alpha-opic quantities.'
          >
            <Select
              defaultValue={
                radianceOrIrradiance === 'radiance'
                  ? 'luminance'
                  : 'illuminance'
              }
              onChange={handleLuminanceOrIlluminance}
            >
              <Option value='luminance'>luminance (cd/m²)</Option>
              <Option value='illuminance'>illuminance (lx)</Option>
            </Select>
          </Form.Item>

          <Form.Item label='Measurement Value' name='relativePower'>
            <InputNumber
              min={1}
              defaultValue={relativePower}
              onChange={handleRelativePower}
            />
            {/* {radianceOrIrradiance === 'radiance' ? '[cd/m²]' : '[lx]'}. */}
          </Form.Item>
        </Fragment>
      )}
    </Form>
  );
};

export default EditAlphaOpicData;

const alphaOpicDataForm = (location) => css`
  ${location === '/results' &&
  css`
    display: flex;
    justify-content: space-between;

    .ant-form-item {
      width: 25rem;
    }
  `}
  .ant-form-item {
    margin: 0;
    display: block;
  }

  .ant-select {
    width: 100% !important;
    margin: 0 !important;
  }

  .ant-form-item-label {
    label {
      margin: 0;
    }
  }
`;
